import { ClickAwayListener, Popper } from "@mui/material";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import CalendarIcon from "@redotech/redo-icons/calendar-date.svg";
import { getDateString } from "@redotech/util/date-format";
import classNames from "classnames";
import { memo, useMemo, useState } from "react";
import { Flex } from "../../flex";
import { RedoButton, RedoButtonSize } from "../buttons/redo-button";
import { DatePicker } from "./date-picker/date-picker-menu";
import * as styles from "./redo-single-date-picker.module.css";

export interface RedoSingleDatePickerProps {
  date: Temporal.PlainDate | undefined;
  disabled?: boolean;
  setDate: (date: Temporal.PlainDate) => void;
  buttonSize?: RedoButtonSize;
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
  allowNoDateOption?: boolean;
  dateStringOptions?: Pick<
    Intl.DateTimeFormatOptions,
    "year" | "month" | "day"
  >;
  /** Show Active/Expired badge based on whether date is in future or past */
  showStatus?: boolean;
}

export const RedoSingleDatePicker = memo(function RedoSingleDatePicker({
  date,
  disabled,
  setDate,
  minDate,
  maxDate,
  buttonSize = "lg",
  allowNoDateOption = false,
  dateStringOptions = {},
  showStatus = false,
}: RedoSingleDatePickerProps) {
  const { i18n, t } = useTranslation();
  const buttonDisplay = date
    ? getDateString(date, { ...dateStringOptions, locale: i18n.language })
    : t("Select date");

  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const [pickerOpen, setPickerOpen] = useState(false);

  const status = useMemo(() => {
    if (!showStatus || !date) {
      return null;
    }
    const today = Temporal.Now.plainDateISO();
    return Temporal.PlainDate.compare(date, today) > 0 ? "active" : "expired";
  }, [showStatus, date]);

  const handleSetCustomDate = useHandler((value: Temporal.PlainDate | null) => {
    if (!value) {
      return;
    }
    setDate(value);
    setPickerOpen(false);
  });

  const handleClick = useHandler((e: React.MouseEvent) => {
    e.stopPropagation();
    setPickerOpen((open) => !open);
  });

  const statusChip = status && (
    <div
      className={classNames(styles.statusChip, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [styles.active]: status === "active",
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [styles.expired]: status === "expired",
      })}
    >
      {status === "active" ? t("Active") : t("Expired")}
    </div>
  );

  return (
    <>
      <Flex
        align="center"
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        className={classNames(styles.container, { [styles.hasStatus]: status })}
        gap="none"
      >
        {statusChip}
        <RedoButton
          centerItems={false}
          disabled={disabled}
          hierarchy="secondary"
          IconLeading={CalendarIcon}
          onClick={handleClick}
          ref={setDropdownAnchor}
          size={buttonSize}
          text={buttonDisplay}
        />
      </Flex>
      <ClickAwayListener onClickAway={() => setPickerOpen(false)}>
        <Popper
          anchorEl={dropdownAnchor}
          modifiers={[
            { name: "offset", options: { offset: [0, 8] } },
            { name: "flip", enabled: true },
            {
              name: "preventOverflow",
              options: { altAxis: true, tether: false, padding: 8 },
            },
          ]}
          open={pickerOpen}
          placement="bottom-start"
        >
          <Flex align="flex-start" maxHeight="screen" overflowY="auto" p="xs">
            <DatePicker
              allowNoDateOption={allowNoDateOption}
              maxDate={maxDate}
              minDate={minDate}
              onDateChange={handleSetCustomDate}
              selectedDate={date || null}
            />
          </Flex>
        </Popper>
      </ClickAwayListener>
    </>
  );
});
